import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CameraService, CameraDto, LicensePlateRecognitionService, PointDirection, ListCamerasRequest, CameraType } from '../../api/gen';
import { JJamesCardComponent, JjamesButtonComponent } from 'shared-lib';
import { ExportService } from './export.service';

@Component({
    selector: 'app-camera-export',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatSnackBarModule,
        MatCheckboxModule,
        JJamesCardComponent,
        JjamesButtonComponent,
    ],
    templateUrl: './camera-export.component.html',
    styleUrl: './camera-export.component.scss'
})
export class CameraExportComponent implements OnInit {
    private cameraService = inject(CameraService);
    private lprService = inject(LicensePlateRecognitionService);
    private snackBar = inject(MatSnackBar);
    readonly exportService = inject(ExportService);

    readonly MAX_DATE_RANGE_DAYS = 7;
    readonly MAX_RECORDS = 5000;

    cameras = signal<CameraDto[]>([]);
    selectedCameraIds = signal<string[]>([]);
    isLoadingCameras = signal(false);

    startDate = signal<string>('');
    endDate = signal<string>('');
    plateNumber = signal<string>('');
    direction = signal<PointDirection | ''>('');
    includeImages = signal(true);

    previewCount = signal<number | null>(null);
    isPreviewing = signal(false);
    dateRangeError = signal<string | null>(null);
    isExporting = computed(() => {
        const s = this.exportService.status();
        return s === 'fetching-metadata' || s === 'downloading-images' || s === 'building-zip';
    });

    progressPercent = computed(() => {
        const total = this.exportService.totalImages();
        const done = this.exportService.downloadedImages();
        if (total === 0) return 0;
        return Math.round((done / total) * 100);
    });

    statusMessage = computed(() => {
        switch (this.exportService.status()) {
            case 'fetching-metadata': return 'Fetching detection records…';
            case 'downloading-images': return `Downloading images (${this.exportService.downloadedImages()} / ${this.exportService.totalImages()})…`;
            case 'building-zip': return 'Building ZIP file…';
            case 'done': return 'Export complete!';
            case 'error': return 'Export failed. Please try again.';
            default: return '';
        }
    });

    ngOnInit(): void {
        this.exportService.resetState();
        this.loadCameras();
    }

    loadCameras(): void {
        this.isLoadingCameras.set(true);
        this.cameraService.camerasFilterPost(
            { pageSize: 200, pageIndex: 0, cameraType: CameraType.VZenith } as ListCamerasRequest
        ).subscribe({
            next: (result) => {
                this.cameras.set(result.items ?? []);
                this.isLoadingCameras.set(false);
            },
            error: (err) => {
                console.error('Error loading cameras', err);
                this.snackBar.open('Error loading cameras', 'Close', { duration: 3000 });
                this.isLoadingCameras.set(false);
            }
        });
    }

    selectAll(): void {
        this.selectedCameraIds.set(this.cameras().map(c => c.id!).filter(Boolean));
    }

    deselectAll(): void {
        this.selectedCameraIds.set([]);
    }

    toggleCamera(id: string): void {
        const current = this.selectedCameraIds();
        if (current.includes(id)) {
            this.selectedCameraIds.set(current.filter(c => c !== id));
        } else {
            this.selectedCameraIds.set([...current, id]);
        }
    }

    isCameraSelected(id: string): boolean {
        return this.selectedCameraIds().includes(id);
    }

    validateDateRange(): boolean {
        const start = this.startDate();
        const end = this.endDate();
        if (!start || !end) {
            this.dateRangeError.set('Start date and end date are required.');
            return false;
        }
        const diffMs = new Date(end).getTime() - new Date(start).getTime();
        if (diffMs < 0) {
            this.dateRangeError.set('End date must be after start date.');
            return false;
        }
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays > this.MAX_DATE_RANGE_DAYS) {
            this.dateRangeError.set(`Date range cannot exceed ${this.MAX_DATE_RANGE_DAYS} days.`);
            return false;
        }
        this.dateRangeError.set(null);
        return true;
    }

    async preview(): Promise<void> {
        if (this.selectedCameraIds().length === 0) {
            this.snackBar.open('Please select at least one camera.', 'Close', { duration: 3000 });
            return;
        }
        if (!this.validateDateRange()) return;

        this.isPreviewing.set(true);
        this.previewCount.set(null);
        try {
            const count = await this.exportService.getPreviewCount({
                cameraIds: this.selectedCameraIds(),
                startDate: this.startDate(),
                endDate: this.endDate(),
                plateNumber: this.plateNumber() || undefined,
                direction: (this.direction() as PointDirection) || undefined,
            });
            this.previewCount.set(count);
        } catch {
            this.snackBar.open('Error fetching preview count.', 'Close', { duration: 3000 });
        } finally {
            this.isPreviewing.set(false);
        }
    }

    async download(): Promise<void> {
        if (!this.validateDateRange()) return;
        if (this.selectedCameraIds().length === 0) {
            this.snackBar.open('Please select at least one camera.', 'Close', { duration: 3000 });
            return;
        }
        this.exportService.resetState();
        await this.exportService.export({
            cameraIds: this.selectedCameraIds(),
            startDate: this.startDate(),
            endDate: this.endDate(),
            plateNumber: this.plateNumber() || undefined,
            direction: (this.direction() as PointDirection) || undefined,
            includeImages: this.includeImages(),
        });
    }

    get downloadDisabled(): boolean {
        const count = this.previewCount();
        return (
            this.isExporting() ||
            this.isPreviewing() ||
            count === null ||
            count > this.MAX_RECORDS ||
            count === 0
        );
    }
}
